import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Divider, Stack, Paper, Checkbox, FormControlLabel, Snackbar, ButtonGroup
} from '@mui/material';
import {
  Add, Search, Refresh, Receipt, CheckCircle, Payment, Visibility,
  Print, ThumbUp, Close, Autorenew, SelectAll, ClearAll, Email, DeleteForever, MergeType, GroupWork,
  Calculate, CalendarMonth, CallMade, AccessTime, FileDownload,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';
import { PaymentDialog } from '../payments/PaymentDialog';
// @ts-ignore
import html2pdf from 'html2pdf.js';

const STATUS_COLOR: any = { draft: 'default', sent: 'info', paid: 'success', overdue: 'error', cancelled: 'warning', disputed: 'error' };
const fmt = (n: any) => Number(n || 0).toFixed(2);
const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const PRINT_STYLE = `
@media print {
  body * { visibility: hidden !important; }
  #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
  #invoice-print-area { position: fixed; top: 0; left: 0; width: 100%; padding: 24px; background: white; }
  .no-print { display: none !important; }
  @page { size: A4; margin: 15mm; }
}`;

// ── Generate CDR Invoice Dialog ─────────────────────────────────────────────
// Builds an invoice for a single customer from their CDRs + active subscriptions
// for a chosen month or custom date range.
const GenerateCDRInvoiceDialog: React.FC<{ open: boolean; onClose: () => void; onCreated: () => void }> = ({ open, onClose, onCreated }) => {
  const [customers, setCustomers] = React.useState<any[]>([]);
  const [customerId, setCustomerId] = React.useState('');
  const [periodStart, setPeriodStart] = React.useState('');
  const [periodEnd, setPeriodEnd] = React.useState('');
  const [currency, setCurrency] = React.useState('AED');
  const [notes, setNotes] = React.useState('');
  const [dueDays, setDueDays] = React.useState('30');
  const [preview, setPreview] = React.useState<any>(null);
  const [previewing, setPreviewing] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [error, setError] = React.useState('');

  // Build last 12 month quick-picks
  const monthOptions = React.useMemo(() => {
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const y = d.getFullYear();
      const m = d.getMonth();
      const start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const endDay = new Date(y, m + 1, 0).getDate();
      const end = `${y}-${String(m + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
      opts.push({ label: d.toLocaleString('en-GB', { month: 'short', year: 'numeric' }), start, end });
    }
    return opts;
  }, []);

  const selectMonth = (start: string, end: string) => {
    setPeriodStart(start); setPeriodEnd(end); setPreview(null);
  };

  const isMonthActive = (start: string) => periodStart === start;

  React.useEffect(() => {
    if (open) {
      api.get('/tenants?limit=200&tier=customer').then(r => setCustomers(r.data.data || [])).catch(() => {});
      // Default to current month
      const d = new Date();
      const y = d.getFullYear();
      const m = d.getMonth();
      const start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const endDay = new Date(y, m + 1, 0).getDate();
      const end = `${y}-${String(m + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
      setPeriodStart(start); setPeriodEnd(end);
    }
  }, [open]);

  const handlePreview = async () => {
    if (!customerId || !periodStart || !periodEnd) return;
    setPreviewing(true); setError(''); setPreview(null);
    try {
      const { data } = await api.post('/invoices/bundled/preview', {
        customer_ids: [customerId], period_start: periodStart, period_end: periodEnd,
      });
      setPreview(data);
    } catch (e: any) { setError(e.response?.data?.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!preview) return;
    setGenerating(true); setError('');
    try {
      await api.post('/invoices/bundled', {
        recipient_tenant_id: customerId,
        customer_ids: [customerId],
        period_start: periodStart,
        period_end: periodEnd,
        currency,
        notes: notes || undefined,
        due_days: parseInt(dueDays) || 30,
      });
      onCreated();
      handleClose();
    } catch (e: any) { setError(e.response?.data?.message || 'Generate failed'); }
    finally { setGenerating(false); }
  };

  const handleClose = () => {
    setPreview(null); setError(''); setCustomerId('');
    setNotes(''); onClose();
  };

  const summary = preview?.customerSummaries?.[0];
  const lineItems: any[] = preview?.lineItems || [];
  const cdrLines = lineItems.filter((l: any) => l.type === 'cdr');
  const subLines = lineItems.filter((l: any) => l.type === 'subscription');
  const selectedCustomer = customers.find((c: any) => c.id === customerId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Calculate sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>Generate Invoice from CDRs</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a customer and billing period — CDR charges + active subscriptions will be combined into a draft invoice
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* ── Customer + settings ── */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>1. Customer</Typography>
            <TextField select fullWidth size="small" label="Select customer" value={customerId}
              onChange={e => { setCustomerId(e.target.value); setPreview(null); }} sx={{ mb: 2 }}>
              <MenuItem value=""><em>Select…</em></MenuItem>
              {customers.map((t: any) => (
                <MenuItem key={t.id} value={t.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.code}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>3. Invoice Settings</Typography>
            <Stack spacing={1.5}>
              <TextField select fullWidth size="small" label="Currency" value={currency}
                onChange={e => setCurrency(e.target.value)}>
                <MenuItem value="AED">AED — UAE Dirham</MenuItem>
                <MenuItem value="USD">USD — US Dollar</MenuItem>
              </TextField>
              <TextField select fullWidth size="small" label="Payment Terms" value={dueDays}
                onChange={e => setDueDays(e.target.value)}>
                <MenuItem value="15">Net 15</MenuItem>
                <MenuItem value="30">Net 30</MenuItem>
                <MenuItem value="45">Net 45</MenuItem>
                <MenuItem value="60">Net 60</MenuItem>
              </TextField>
              <TextField fullWidth size="small" label="Notes (optional)" value={notes}
                onChange={e => setNotes(e.target.value)} multiline rows={2} />
            </Stack>
          </Grid>

          {/* ── Period picker ── */}
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              <CalendarMonth fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              2. Billing Period
            </Typography>

            {/* Quick month buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {monthOptions.map(opt => (
                <Chip
                  key={opt.start}
                  label={opt.label}
                  onClick={() => selectMonth(opt.start, opt.end)}
                  color={isMonthActive(opt.start) ? 'primary' : 'default'}
                  variant={isMonthActive(opt.start) ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ cursor: 'pointer', fontWeight: isMonthActive(opt.start) ? 700 : 400 }}
                />
              ))}
            </Box>

            {/* Custom date range */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Or enter a custom date range:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="From" type="date" value={periodStart}
                onChange={e => { setPeriodStart(e.target.value); setPreview(null); }}
                InputLabelProps={{ shrink: true }} />
              <TextField fullWidth size="small" label="To" type="date" value={periodEnd}
                onChange={e => { setPeriodEnd(e.target.value); setPreview(null); }}
                InputLabelProps={{ shrink: true }} />
            </Stack>

            <Button fullWidth variant="outlined" onClick={handlePreview}
              disabled={previewing || !customerId || !periodStart || !periodEnd}
              startIcon={previewing ? <CircularProgress size={16} /> : <Visibility />}>
              {previewing ? 'Loading preview…' : 'Preview Charges'}
            </Button>

            {/* ── Preview results ── */}
            {preview && summary && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Charge Preview — {selectedCustomer?.name}
                </Typography>

                {/* CDR summary row */}
                {summary.cdr_calls > 0 ? (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'primary.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CallMade fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>VoIP / CDR Charges</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {summary.cdr_calls.toLocaleString()} calls · {summary.cdr_minutes.toLocaleString()} minutes
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body1" fontWeight={700} color="primary.main">
                        {currency} {fmt(summary.cdr_total)}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <Alert severity="info" sx={{ mb: 1 }} icon={<CallMade />}>
                    No answered CDRs found for this period
                  </Alert>
                )}

                {/* Subscription lines */}
                {subLines.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <AccessTime fontSize="small" color="secondary" />
                      <Typography variant="body2" fontWeight={600}>Active Subscriptions</Typography>
                    </Box>
                    {subLines.map((l: any, i: number) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="caption">{l.description?.replace(selectedCustomer?.name + ' — ', '')}</Typography>
                        <Typography variant="caption" fontWeight={600}>{currency} {fmt(l.amount)}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Grand total */}
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={700}>Invoice Total</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {currency} {fmt(preview.grandTotal)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    CDR: {fmt(summary.cdr_total)} + Subscriptions: {fmt(summary.sub_total)} · Status: Draft · Due: Net {dueDays}
                  </Typography>
                </Paper>
              </Box>
            )}

            {preview && !summary && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No chargeable activity found for this customer and period. The invoice would be zero-value.
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleGenerate}
          disabled={generating || !preview || !customerId}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Calculate />}>
          {generating ? 'Generating…' : `Generate Draft Invoice${preview ? ` · ${currency} ${fmt(preview.grandTotal)}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Bundled Invoice Dialog ──────────────────────────────────────────────────
const BundledInvoiceDialog: React.FC<{ open: boolean; onClose: () => void; onCreated: () => void }> = ({ open, onClose, onCreated }) => {
  const [allTenants, setAllTenants] = React.useState<any[]>([]);
  const [recipient, setRecipient] = React.useState('');
  const [customers, setCustomers] = React.useState<string[]>([]);
  const [periodStart, setPeriodStart] = React.useState('');
  const [periodEnd, setPeriodEnd] = React.useState('');
  const [currency, setCurrency] = React.useState('AED');
  const [preview, setPreview] = React.useState<any>(null);
  const [previewing, setPreviewing] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) api.get('/tenants?limit=200').then(r => setAllTenants(r.data.data || [])).catch(() => {});
  }, [open]);

  const customerList = allTenants.filter(t => ['customer', 'reseller'].includes(t.tier));
  const recipientList = allTenants.filter(t => t.tier !== 'super' && t.tier !== 'provider');

  const handlePreview = async () => {
    if (!customers.length || !periodStart || !periodEnd) return;
    setPreviewing(true); setError(''); setPreview(null);
    try {
      const { data } = await api.post('/invoices/bundled/preview', {
        customer_ids: customers, period_start: periodStart, period_end: periodEnd,
      });
      setPreview(data);
    } catch (e: any) { setError(e.response?.data?.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      await api.post('/invoices/bundled', {
        recipient_tenant_id: recipient, customer_ids: customers,
        period_start: periodStart, period_end: periodEnd, currency,
      });
      onCreated(); handleClose();
    } catch (e: any) { setError(e.response?.data?.message || 'Generate failed'); }
    finally { setGenerating(false); }
  };

  const handleClose = () => {
    setPreview(null); setError(''); setRecipient(''); setCustomers([]);
    setPeriodStart(''); setPeriodEnd(''); onClose();
  };

  const toggleCustomer = (id: string) =>
    setCustomers(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupWork sx={{ color: 'secondary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>Bundled Invoice</Typography>
            <Typography variant="body2" color="text.secondary">Consolidate multiple customers into one invoice for a reseller</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {/* Left: Config */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>1. Recipient (Bill To)</Typography>
            <TextField select fullWidth size="small" label="Recipient tenant" value={recipient}
              onChange={e => setRecipient(e.target.value)} sx={{ mb: 2 }}>
              <MenuItem value=""><em>Select…</em></MenuItem>
              {recipientList.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={t.tier} size="small" color={t.tier === 'reseller' ? 'secondary' : 'default'} sx={{ height: 18, fontSize: 10 }} />
                    {t.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="subtitle2" fontWeight={700} gutterBottom>2. Billing Period</Typography>
            <TextField fullWidth size="small" label="Period Start" type="date" value={periodStart}
              onChange={e => { setPeriodStart(e.target.value); setPreview(null); }}
              InputLabelProps={{ shrink: true }} sx={{ mb: 1 }} />
            <TextField fullWidth size="small" label="Period End" type="date" value={periodEnd}
              onChange={e => { setPeriodEnd(e.target.value); setPreview(null); }}
              InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />

            <TextField select fullWidth size="small" label="Currency" value={currency}
              onChange={e => setCurrency(e.target.value)} sx={{ mb: 2 }}>
              <MenuItem value="AED">AED — UAE Dirham</MenuItem>
              <MenuItem value="USD">USD — US Dollar</MenuItem>
            </TextField>

            <Button fullWidth variant="outlined" color="primary" onClick={handlePreview}
              disabled={previewing || !customers.length || !periodStart || !periodEnd}
              startIcon={previewing ? <CircularProgress size={16} /> : <Visibility />}>
              {previewing ? 'Loading preview…' : `Preview Charges (${customers.length} selected)`}
            </Button>
          </Grid>

          {/* Right: Customer selection */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                3. Select Customers
                {customers.length > 0 && <Chip label={customers.length} size="small" color="primary" sx={{ ml: 1, height: 18 }} />}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => { setCustomers(customerList.map(t => t.id)); setPreview(null); }}>All</Button>
                <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => { setCustomers([]); setPreview(null); }}>None</Button>
              </Stack>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 260, overflowY: 'auto' }}>
              {customerList.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
              )}
              {customerList.map(t => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.3, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => { toggleCustomer(t.id); setPreview(null); }}>
                  <Checkbox size="small" checked={customers.includes(t.id)} onChange={() => {}} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{t.name}</Typography>
                  <Chip label={t.tier} size="small" variant="outlined" sx={{ height: 16, fontSize: 10, mr: 0.5 }} />
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Preview results */}
          {preview && preview.customerSummaries?.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Charge Preview</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100', py: 0.8 } }}>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">CDR (calls / min)</TableCell>
                    <TableCell align="right">CDR Charges</TableCell>
                    <TableCell align="right">Subscriptions</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.customerSummaries.map((c: any) => (
                    <TableRow key={c.customer_id} sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: 12 }}>
                        {c.cdr_calls > 0 ? `${c.cdr_calls} / ${c.cdr_minutes}m` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {c.cdr_total > 0 ? fmt(c.cdr_total) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {c.sub_total > 0 ? fmt(c.sub_total) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {fmt(c.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                    <TableCell colSpan={4}>GRAND TOTAL ({currency})</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'primary.main', fontSize: '1.05em' }}>
                      {currency} {fmt(preview.grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          )}
          {preview && preview.customerSummaries?.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">No chargeable activity found for the selected customers and period.</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleGenerate}
          disabled={generating || !preview || !recipient || !preview.grandTotal}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <MergeType />}>
          {generating ? 'Generating…' : 'Generate Bundled Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Invoice Preview Dialog ──────────────────────────────────────────────────
const InvoicePreview: React.FC<{ invoiceId: string; onClose: () => void; onApproved: () => void }> = ({ invoiceId, onClose, onApproved }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuthStore();
  const canWrite = user?.role !== 'executive';

  const loadDetail = useCallback(() => {
    setLoading(true);
    api.get(`/invoices/${invoiceId}/detail`)
      .then(r => setDetail(r.data))
      .catch(() => setError('Failed to load invoice detail'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const BANK = {
    name:     'SYNERGYTALK FZ LLC',
    iban:     'AE66 0400 0003 5341 8591 001',
    branch:   'Al Quoz, Dubai',
    currency: 'AED',
  };

  const [emailSending, setEmailSending] = React.useState(false);
  const [attachCdrs, setAttachCdrs] = React.useState(true);
  const [cdrBusy, setCdrBusy] = React.useState(false);

  // Build a customer-facing (sell-side only) CDR CSV for this invoice's period.
  // Uses the /cdr API so the financial-mask is respected; buy cost / margin are never included.
  const fetchCdrCsv = async (): Promise<{ csv: string; count: number }> => {
    const inv2 = detail;
    const p = new URLSearchParams({
      tenant_id: inv2.tenant_id,
      from: new Date(inv2.period_start).toISOString(),
      to: new Date(inv2.period_end).toISOString(),
      status: 'answered', limit: '5000',
    });
    const { data: res } = await api.get('/cdr?' + p.toString());
    const rows: any[] = res.data || [];
    const header = ['Date/Time (UTC)', 'From', 'To', 'Duration (s)', 'Billable (min)', 'Rate/min', 'Amount'];
    const esc = (v: any) => { const s = (v === null || v === undefined) ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = rows.map(r => [
      r.start_time ? new Date(r.start_time).toISOString() : '',
      r.src_number || '', r.dst_number || '',
      r.duration ?? '', ((Number(r.billable_duration) || 0) / 60).toFixed(2),
      r.sell_rate ?? '', r.sell_cost ?? '',
    ].map(esc).join(','));
    return { csv: [header.join(',')].concat(lines).join('\n'), count: rows.length };
  };

  const downloadCdrs = async () => {
    setCdrBusy(true);
    try {
      const { csv } = await fetchCdrCsv();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `CDR-${detail?.invoice_number || 'invoice'}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } finally { setCdrBusy(false); }
  };

  const buildEmailBody = (inv2: any, t2: any): string => {
    const num      = inv2.invoice_number || '';
    const amt      = fmt(inv2.total);
    const cur      = inv2.currency || 'USD';
    const period   = `${fmtDate(inv2.period_start)} to ${fmtDate(inv2.period_end)}`;
    const due      = fmtDate(inv2.due_date);
    const custName = t2?.company_name || t2?.name || 'Customer';
    return `Dear ${custName},

Please find attached your invoice for the billing period ${period}.

INVOICE DETAILS
Invoice Number : ${num}
Due Date       : ${due}
Amount Due     : ${cur} ${amt}

PAYMENT INSTRUCTIONS
Account Name   : ${BANK.name}
IBAN           : ${BANK.iban}
Branch         : ${BANK.branch}
Currency       : ${BANK.currency}

Please quote Invoice Number ${num} as your payment reference.
For any queries, please contact us at info@synergytalk.ae.

Best regards,
Synergytalk FZ LLC
Dubai, United Arab Emirates`;
  };

  const openOutlookEmail = async (invoiceData?: any) => {
    const inv2 = invoiceData || inv;
    if (!inv2) return;
    const t2   = inv2.tenant || tenant;
    const to   = t2?.billing_email || t2?.contact_email || '';
    const cc   = t2?.cc_email || '';
    const num  = inv2.invoice_number || '';
    const amt  = fmt(inv2.total);
    const cur  = inv2.currency || 'USD';
    const subject  = `Invoice ${num} - ${cur} ${amt} - Synergytalk FZ LLC`;
    const bodyText = buildEmailBody(inv2, t2);
    const filename = `Invoice-${num}.pdf`;

    setEmailSending(true);
    try {
      // Generate PDF from the rendered invoice HTML
      const element = document.getElementById('invoice-print-area');
      if (!element) throw new Error('Invoice not rendered');

      const pdfBlob: Blob = await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename,
          image: { type: 'jpeg', quality: 0.97 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .outputPdf('blob');

      // Convert PDF blob to base64
      const base64Pdf = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Build EML file — when opened, Outlook launches with email + attachments
      const boundary = `----=_NextPart_${Date.now()}`;

      // Optional masked (sell-side) CDR CSV attachment
      let cdrPart: string[] = [];
      if (attachCdrs) {
        try {
          const { csv } = await fetchCdrCsv();
          const cdrB64 = btoa(unescape(encodeURIComponent(csv)));
          cdrPart = [
            `--${boundary}`,
            `Content-Type: text/csv; name="CDR-${num}.csv"`,
            'Content-Transfer-Encoding: base64',
            `Content-Disposition: attachment; filename="CDR-${num}.csv"`,
            '',
            cdrB64.match(/.{1,76}/g)?.join('\r\n') || cdrB64,
            '',
          ];
        } catch { /* CDR attach is best-effort; still send the invoice */ }
      }

      const eml = [
        'MIME-Version: 1.0',
        `To: ${to}`,
        ...(cc ? [`Cc: ${cc}`] : []),
        `Subject: ${subject}`,
        `Content-Type: multipart/mixed; boundary="${boundary}"`,
        '',
        `--${boundary}`,
        'Content-Type: text/plain; charset=UTF-8',
        'Content-Transfer-Encoding: quoted-printable',
        '',
        bodyText,
        '',
        `--${boundary}`,
        `Content-Type: application/pdf; name="${filename}"`,
        'Content-Transfer-Encoding: base64',
        `Content-Disposition: attachment; filename="${filename}"`,
        '',
        // Split base64 into 76-char lines (RFC 2045)
        base64Pdf.match(/.{1,76}/g)?.join('\r\n') || base64Pdf,
        '',
        ...cdrPart,
        `--${boundary}--`,
      ].join('\r\n');

      const blob = new Blob([eml], { type: 'message/rfc822' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Invoice-${num}.eml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Email generation failed:', err);
      // Fallback: open mailto without attachment
      const link = to
        ? `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`
        : `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
      window.open(link, '_blank');
    } finally {
      setEmailSending(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.patch(`/invoices/${invoiceId}/approve`);
      onApproved();
      // Open email after approval — invoice still visible in DOM
      await openOutlookEmail();
      onClose();
    }
    catch (e: any) { setError(e.response?.data?.message || 'Approve failed'); }
    finally { setApproving(false); }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try { await api.patch(`/invoices/${invoiceId}/regenerate`); loadDetail(); }
    catch (e: any) { setError(e.response?.data?.message || 'Regenerate failed'); }
    finally { setRegenerating(false); }
  };

  const inv = detail;
  const tenant = detail?.tenant || {};
  const breakdown: any[] = detail?.breakdown || [];
  const totalCalls = breakdown.reduce((s, r) => s + parseInt(r.calls || 0), 0);
  const totalMins  = breakdown.reduce((s, r) => s + parseFloat(r.total_minutes || 0), 0);
  const totalCharges = breakdown.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Invoice Preview</Typography>
        <Stack direction="row" spacing={1} className="no-print">
          {canWrite && (
            <Tooltip title="Recalculate totals from CDR data">
              <Button startIcon={regenerating ? <CircularProgress size={14} /> : <Autorenew />}
                onClick={handleRegenerate} variant="outlined" size="small" color="warning" disabled={regenerating}>
                Regenerate
              </Button>
            </Tooltip>
          )}
          <Button startIcon={<Print />} onClick={() => window.print()} variant="outlined" size="small">Print / PDF</Button>
          <Tooltip title="Download the call-detail records (CSV) for this invoice period — to share with the customer on request">
            <Button startIcon={cdrBusy ? <CircularProgress size={14} /> : <FileDownload />} onClick={downloadCdrs} variant="outlined" size="small" disabled={cdrBusy}>
              {cdrBusy ? 'Building…' : 'Download CDRs'}
            </Button>
          </Tooltip>
          <Tooltip title="Attach the call-detail CSV to the invoice email">
            <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={attachCdrs} onChange={e => setAttachCdrs(e.target.checked)} />} label={<Typography variant="caption">Attach CDRs</Typography>} />
          </Tooltip>
          <Button
            startIcon={emailSending ? <CircularProgress size={14} /> : <Email />}
            onClick={() => openOutlookEmail()}
            variant="outlined" size="small" color="info"
            disabled={emailSending}>
            {emailSending ? 'Generating PDF...' : 'Email Invoice'}
          </Button>
          {inv?.status === 'draft' && canWrite && (
            <Button startIcon={<ThumbUp />} onClick={handleApprove} variant="contained" color="success" size="small" disabled={approving}>
              {approving ? <CircularProgress size={16} /> : 'Approve & Send'}
            </Button>
          )}
          {inv?.status !== 'paid' && canWrite && (
            <Tooltip title="Delete Invoice">
              <IconButton size="small" color="error" onClick={async () => {
                if (!window.confirm(`Delete invoice ${inv?.invoice_number}? This cannot be undone.`)) return;
                try { await api.delete(`/invoices/${invoiceId}`); onApproved(); onClose(); }
                catch (e: any) { alert((e as any).response?.data?.message || 'Delete failed'); }
              }}>
                <DeleteForever fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {inv && (
          <Box id="invoice-print-area" sx={{ p: 4, bgcolor: 'white', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
              <Box>
                <img src="/synergytalk-logo.svg" alt="Synergytalk" style={{ height: 72, display: 'block' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>Synergytalk FZ LLC</Typography>
                <Typography variant="body2" color="text.secondary">info@synergytalk.ae</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h4" fontWeight={900} sx={{ color: inv.status === 'paid' ? '#2e7d32' : '#1565c0', mb: 0.5 }}>INVOICE</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{inv.invoice_number}</Typography>
                <Chip label={inv.status?.toUpperCase()} size="small" color={STATUS_COLOR[inv.status] || 'default'} sx={{ mt: 0.5 }} />
                <Box sx={{ mt: 1.5, '& > *': { display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 0.3 } }}>
                  <Box><Typography variant="body2" color="text.secondary">Invoice Date:</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(inv.created_at)}</Typography></Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Due Date:</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid' ? 'error.main' : 'inherit' }}>
                      {fmtDate(inv.due_date)} <Typography component="span" variant="caption" color="text.secondary">(15 days)</Typography>
                    </Typography>
                  </Box>
                  <Box><Typography variant="body2" color="text.secondary">Billing Period:</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(inv.period_start)} — {fmtDate(inv.period_end)}</Typography></Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1.5, color: 'text.secondary', fontWeight: 700 }}>From</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>Synergytalk FZ LLC</Typography>
                <Typography variant="body2" color="text.secondary">Dubai, United Arab Emirates</Typography>
                <Typography variant="body2" color="text.secondary">info@synergytalk.ae</Typography>
                <Typography variant="body2" color="text.secondary">TRN: 100XXXXXXXXX003</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1.5, color: 'text.secondary', fontWeight: 700 }}>Bill To</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>{tenant.company_name || tenant.name || '—'}</Typography>
                {tenant.city && <Typography variant="body2" color="text.secondary">{tenant.city}</Typography>}
                {tenant.contact_email && <Typography variant="body2" color="text.secondary">{tenant.contact_email}</Typography>}
                {tenant.contact_phone && <Typography variant="body2" color="text.secondary">{tenant.contact_phone}</Typography>}
                {tenant.code && <Typography variant="body2" color="text.secondary">Account: {tenant.code}</Typography>}
              </Grid>
            </Grid>

            {detail?.is_bundled ? (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>Customer Breakdown</Typography>
                <Table size="small" sx={{ mb: 1, '& th': { bgcolor: '#6a1b9a', color: 'white', fontWeight: 700, py: 1 }, '& td': { py: 0.8 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdown.map((cust: any, ci: number) => (
                      <React.Fragment key={ci}>
                        {cust.items.map((item: any, ii: number) => (
                          <TableRow key={ii} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                            {ii === 0 && (
                              <TableCell rowSpan={cust.items.length} sx={{ fontWeight: 700, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>
                                {cust.name}
                              </TableCell>
                            )}
                            <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                              <Chip label={item.type} size="small" variant="outlined" sx={{ height: 16, fontSize: 10, mr: 0.8 }} />
                              {item.description}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {inv.currency || 'AED'} {fmt(item.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', py: 0.5 }}>
                            {cust.name} subtotal
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, py: 0.5 }}>
                            {inv.currency || 'AED'} {fmt(cust.cdr + cust.sub)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {breakdown.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No line items.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>Call Detail — By Destination</Typography>
                <Table size="small" sx={{ mb: 1, '& th': { bgcolor: '#1565c0', color: 'white', fontWeight: 700, py: 1 }, '& td': { py: 0.8 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Country / Destination</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">Minutes</TableCell>
                      <TableCell align="right">Rate / Min (Customer)</TableCell>
                      <TableCell align="right">Charges</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdown.map((r, i) => (
                      <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{r.destination}</TableCell>
                        <TableCell align="right">{parseInt(r.calls || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{parseFloat(r.total_minutes || 0).toFixed(1)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>${parseFloat(r.rate || 0).toFixed(4)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>${fmt(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                    {breakdown.length > 0 && (
                      <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid #1565c0', bgcolor: '#eef4fb' } }}>
                        <TableCell>Total</TableCell>
                        <TableCell align="right">{totalCalls.toLocaleString()}</TableCell>
                        <TableCell align="right">{totalMins.toFixed(1)}</TableCell>
                        <TableCell align="right">—</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>${fmt(totalCharges)}</TableCell>
                      </TableRow>
                    )}
                    {breakdown.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No CDR breakdown available for this period.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Paper variant="outlined" sx={{ minWidth: 320, p: 2 }}>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Total Calls:</Typography><Typography variant="body2" fontWeight={600}>{totalCalls.toLocaleString()}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Total Minutes:</Typography><Typography variant="body2" fontWeight={600}>{totalMins.toFixed(1)}</Typography></Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Subtotal:</Typography><Typography variant="body2" fontFamily="monospace">${fmt(inv.subtotal)}</Typography></Box>
                  {parseFloat(inv.tax_amount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Tax / VAT:</Typography><Typography variant="body2" fontFamily="monospace">${fmt(inv.tax_amount)}</Typography></Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={700}>TOTAL DUE:</Typography>
                    <Typography variant="h5" fontWeight={900} sx={{ color: '#1565c0', fontFamily: 'monospace' }}>{inv.currency||'AED'} {fmt(inv.total)}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Payment due by <strong>{fmtDate(inv.due_date)}</strong></Typography>
                </Stack>
              </Paper>
            </Box>

            <Box sx={{ bgcolor: '#f5f7fa', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Payment Instructions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Bank Transfer</Typography>
                  <Typography variant="body2" fontWeight={700}>SYNERGYTALK FZ LLC</Typography>
                  <Typography variant="body2">IBAN: AE66 0400 0003 5341 8591 001</Typography>
                  <Typography variant="body2">Branch: Al Quoz, Dubai</Typography>
                  <Typography variant="body2">Currency: AED</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Payment Reference</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={700}>{inv.invoice_number}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>Please quote invoice number in all transfers.</Typography>
                </Grid>
              </Grid>
            </Box>

            {inv.notes && <Box sx={{ mt: 1 }}><Typography variant="caption" color="text.secondary">Notes: {inv.notes}</Typography></Box>}

            <Divider sx={{ mt: 3, mb: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <img src="/synergytalk-logo.svg" alt="Synergytalk" style={{ height: 28, opacity: 0.7 }} />
              <Typography variant="caption" color="text.secondary">Synergytalk FZ LLC · Dubai, UAE · info@synergytalk.ae</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main Invoices Page ──────────────────────────────────────────────────────
const empty = { tenant_id: '', period_start: '', period_end: '', due_date: '', notes: '', tax_amount: 0 };

export const InvoicesPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  const [payDialog, setPayDialog] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState<any>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [bundleDialog, setBundleDialog] = useState(false);
  const [cdrDialog, setCdrDialog] = useState(false);
  const { user } = useAuthStore();
  const canWrite = user?.role !== 'executive';

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '25', offset: String(page * 25) });
      if (search) p.set('search', search);
      const { data: res } = await api.get(`/invoices?${p}`);
      setData(res.data || (Array.isArray(res) ? res : []));
      setTotal(res.total || 0);
      setSelected([]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const d = new Date(); d.setDate(d.getDate() + 15);
    setForm(f => ({ ...f, due_date: d.toISOString().slice(0, 10) }));
    api.get('/tenants?limit=200&tier=customer').then(({ data: r }) => setTenants(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setError('');
    try { await api.post('/invoices', form); setDialog(false); load(); }
    catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const markPaid = async (row: any) => {
    if (!window.confirm(`Mark invoice ${row.invoice_number} as PAID?`)) return;
    await api.patch(`/invoices/${row.id}/pay`); load();
  };

  const approve = async (row: any) => {
    if (!window.confirm(`Approve invoice ${row.invoice_number} and mark as Sent?`)) return;
    try { await api.patch(`/invoices/${row.id}/approve`); load(); }
    catch (e: any) { alert(e.response?.data?.message || 'Approve failed'); }
  };

  const deleteInvoice = async (row: any) => {
    if (!window.confirm(`Delete invoice ${row.invoice_number}? This cannot be undone.`)) return;
    try { await api.delete(`/invoices/${row.id}`); load(); }
    catch (e: any) { alert(e.response?.data?.message || 'Delete failed'); }
  };

  const regenerateOne = async (id: string) => {
    setRegeneratingId(id);
    try {
      const { data: res } = await api.patch(`/invoices/${id}/regenerate`);
      setToast(`Invoice regenerated — new total: ${res.currency||''} ${fmt(res.total)}`);
      load();
    } catch (e: any) { setToast('Regenerate failed: ' + (e.response?.data?.message || e.message)); }
    finally { setRegeneratingId(null); }
  };

  const regenerateSelected = async () => {
    if (!selected.length) return;
    setRegenerating(true);
    try {
      const { data: res } = await api.post('/invoices/regenerate-bulk', { ids: selected });
      setToast(`${res.updated} invoice(s) regenerated — total revenue: ${fmt(res.total_revenue)}`);
      load();
    } catch (e: any) { setToast('Regenerate failed: ' + (e.response?.data?.message || e.message)); }
    finally { setRegenerating(false); }
  };

  const regenerateAll = async () => {
    if (!window.confirm('Regenerate ALL invoices (except paid/cancelled) from CDR data? This will update all totals.')) return;
    setRegenerating(true);
    try {
      const { data: res } = await api.post('/invoices/regenerate-bulk', { all: true });
      setToast(`${res.updated} invoice(s) regenerated — total revenue: ${fmt(res.total_revenue)}`);
      load();
    } catch (e: any) { setToast('Regenerate failed: ' + (e.response?.data?.message || e.message)); }
    finally { setRegenerating(false); }
  };

  const allSelected = data.length > 0 && selected.length === data.length;
  const someSelected = selected.length > 0 && !allSelected;

  const toggleAll = () => setSelected(allSelected ? [] : data.map(r => r.id));
  const toggleOne = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Receipt sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Invoices</Typography>
            <Typography variant="body2" color="text.secondary">{total} invoices</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <TextField size="small" placeholder="Search..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          {canWrite && (
            <>
              {selected.length > 0 && (
                <Button variant="contained" color="warning"
                  startIcon={regenerating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Autorenew />}
                  onClick={regenerateSelected} disabled={regenerating}>
                  Regenerate {selected.length} Selected
                </Button>
              )}
              <Button variant="outlined" color="warning"
                startIcon={regenerating ? <CircularProgress size={16} /> : <Autorenew />}
                onClick={regenerateAll} disabled={regenerating}>
                Regenerate All
              </Button>
              <Button variant="contained" color="success" startIcon={<Calculate />}
                onClick={() => setCdrDialog(true)}>
                Generate from CDRs
              </Button>
              <Button variant="contained" color="secondary" startIcon={<MergeType />}
                onClick={() => setBundleDialog(true)}>
                Bundled Invoice
              </Button>
              <Button variant="contained" startIcon={<Add />}
                onClick={() => { setForm({ ...empty, due_date: new Date(Date.now() + 15*86400000).toISOString().slice(0,10) }); setError(''); setDialog(true); }}>
                New Invoice
              </Button>
            </>
          )}
        </Box>
      </Box>

      {selected.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" startIcon={<ClearAll />} onClick={() => setSelected([])}>Clear</Button>}>
          {selected.length} invoice{selected.length > 1 ? 's' : ''} selected
        </Alert>
      )}

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canWrite && (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                    </TableCell>
                  )}
                  {['Invoice #', 'Customer', 'Period', 'Subtotal', 'Total', 'Billing Date', 'Due Date', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => {
                  const isSelected = selected.includes(row.id);
                  return (
                    <TableRow key={row.id} hover selected={isSelected}>
                      {canWrite && (
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={isSelected} onChange={() => toggleOne(row.id)} />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {row.invoice_number}
                        {row.source === 'bundled' && <Chip label="BUNDLED" size="small" color="secondary" sx={{ ml: 0.8, height: 16, fontSize: 9 }} />}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{tenants.find(t => t.id === row.tenant_id)?.name || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {fmtDate(row.period_start)} — {fmtDate(row.period_end)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.currency||'AED'} {fmt(row.subtotal)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1565c0' }}>{row.currency||'AED'} {fmt(row.total)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtDate(row.created_at)}</TableCell>
                      <TableCell sx={{ color: row.due_date && new Date(row.due_date) < new Date() && row.status !== 'paid' ? 'error.main' : 'text.secondary', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {fmtDate(row.due_date)}
                      </TableCell>
                      <TableCell><Chip label={row.status} size="small" color={STATUS_COLOR[row.status] || 'default'} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Preview Invoice">
                          <IconButton size="small" onClick={() => setPreviewId(row.id)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canWrite && (
                          <Tooltip title="Regenerate totals from CDR data">
                            <IconButton size="small" color="warning"
                              onClick={() => regenerateOne(row.id)}
                              disabled={regeneratingId === row.id}>
                              {regeneratingId === row.id
                                ? <CircularProgress size={16} />
                                : <Autorenew fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.status === 'draft' && canWrite && (
                          <Tooltip title="Approve & Send">
                            <IconButton size="small" color="success" onClick={() => approve(row)}>
                              <ThumbUp fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.status !== 'paid' && row.status !== 'cancelled' && canWrite && (
                          <>
                            <Tooltip title="Record payment received (Customer Accounts)">
                              <IconButton size="small" color="primary" onClick={() => { window.location.href = `/accounts?customer=${row.tenant_id}`; }}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {row.status !== 'paid' && canWrite && (
                          <Tooltip title="Delete Invoice">
                            <IconButton size="small" color="error" onClick={() => deleteInvoice(row)}>
                              <DeleteForever fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canWrite ? 10 : 9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No invoices yet. Use Billing Import → Generate Invoices to create invoices from CDR data.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={25}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {previewId && <InvoicePreview invoiceId={previewId} onClose={() => setPreviewId(null)} onApproved={load} />}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Invoice</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Customer *" value={form.tenant_id}
                onChange={e => setForm({ ...form, tenant_id: e.target.value })}>
                <MenuItem value=""><em>Select customer…</em></MenuItem>
                {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Period Start *" type="date" value={form.period_start}
                onChange={e => setForm({ ...form, period_start: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Period End *" type="date" value={form.period_end}
                onChange={e => setForm({ ...form, period_end: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Due Date (15 days)" type="date" value={form.due_date}
                onChange={e => setForm({ ...form, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Tax Amount" value={form.tax_amount}
                onChange={e => setForm({ ...form, tax_amount: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Notes" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.tenant_id || !form.period_start || !form.period_end}>
            {saving ? <CircularProgress size={20} /> : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <GenerateCDRInvoiceDialog open={cdrDialog} onClose={() => setCdrDialog(false)} onCreated={load} />
      <BundledInvoiceDialog open={bundleDialog} onClose={() => setBundleDialog(false)} onCreated={load} />

      <PaymentDialog open={payDialog} invoice={payingInvoice} tenants={tenants}
        onClose={() => setPayDialog(false)} onSuccess={() => load()} />

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast} />
    </Box>
  );
};
